import { useRef, useState } from "react";
import type { ChangeEvent } from "react";
import { Navigate, useParams } from "react-router-dom";
import { Btn } from "@/components/ui/Btn/Btn";
import { Icon } from "@/components/ui/Icon/Icon";
import { Input } from "@/components/ui/Input/Input";
import { AppShell } from "@/components/app/AppShell";
import { useAuth } from "@/lib/AuthContext";
import {
  mediaId,
  useBrand,
  useCreateMedia,
  useDeleteMedia,
  useMedia,
  useUpdateMedia,
  useWorkspace,
} from "@/lib/queries";
import type { MediaItem } from "@/lib/queries";
import type { MediaOrigin, MediaPurpose } from "@notamen/core/types";
import styles from "./Media.module.css";

const PURPOSES: { value: MediaPurpose; label: string }[] = [
  { value: "product", label: "Product" },
  { value: "logo", label: "Logo" },
  { value: "lifestyle", label: "Lifestyle" },
  { value: "other", label: "Other" },
];

type Filter = MediaPurpose | "all";
type OriginFilter = MediaOrigin | "all";

const ORIGINS: { value: OriginFilter; label: string }[] = [
  { value: "all", label: "All sources" },
  { value: "uploaded", label: "Uploaded" },
  { value: "generated", label: "Generated" },
];

export function Media() {
  const { ws, brand } = useParams<{ ws: string; brand: string }>();
  const auth = useAuth();
  const brandQuery = useBrand(ws, brand);
  const wsQuery = useWorkspace(ws);

  const [filter, setFilter] = useState<Filter>("all");
  const [origin, setOrigin] = useState<OriginFilter>("all");
  const mediaQuery = useMedia(ws, brand, filter === "all" ? undefined : filter);

  const [uploadPurpose, setUploadPurpose] = useState<MediaPurpose>("product");
  const [uploadDescription, setUploadDescription] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);
  const createMedia = useCreateMedia(ws, brand);

  if (auth.status === "anonymous") {
    return <Navigate to={`/signin?next=/w/${ws}/b/${brand}/media`} replace />;
  }

  const b = brandQuery.data?.brand;
  const role = wsQuery.data?.role;
  const canManage = role === "owner" || role === "admin" || role === "editor";
  // Origin filter is client-side: every MediaRecord carries `origin`, and the
  // list endpoint only filters by purpose. Legacy rows written before
  // generation existed have no origin — treat absent as "uploaded".
  const allMedia = mediaQuery.data?.media ?? [];
  const media =
    origin === "all"
      ? allMedia
      : allMedia.filter((m) => (m.origin ?? "uploaded") === origin);

  function onFileChosen(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    createMedia.mutate(
      {
        file,
        purpose: uploadPurpose,
        ...(uploadDescription.trim() ? { description: uploadDescription.trim() } : {}),
      },
      {
        onSuccess: () => {
          setUploadDescription("");
          if (fileRef.current) fileRef.current.value = "";
        },
      },
    );
  }

  return (
    <AppShell currentWorkspaceId={ws}>
      <div className={styles.page}>
        <Btn href={`/w/${ws}/b/${brand}`} variant="ghost" size="sm" iconLeft={<Icon name="arrow-left" size={13} />}>
          Back to brand
        </Btn>

        <header className={styles.head}>
          <h1 className={styles.title}>
            <Icon name="image" size={20} /> Media{b ? ` · ${b.name}` : ""}
          </h1>
        </header>

        {canManage && (
          <div className={styles.uploader}>
            <div className={styles.uploaderField}>
              <span className={styles.uploaderLabel}>Purpose</span>
              <select
                className={styles.select}
                value={uploadPurpose}
                onChange={(e) => setUploadPurpose(e.target.value as MediaPurpose)}
                disabled={createMedia.isPending}
              >
                {PURPOSES.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </select>
            </div>

            <div className={`${styles.uploaderField} ${styles.descField}`}>
              <span className={styles.uploaderLabel}>Description (optional)</span>
              <Input
                value={uploadDescription}
                onChange={(e) => setUploadDescription(e.target.value)}
                placeholder="e.g. Hero shot of the summer collection"
                disabled={createMedia.isPending}
              />
            </div>

            <div className={styles.uploaderField}>
              <span className={styles.uploaderLabel}>File</span>
              <input
                ref={fileRef}
                className={styles.fileInput}
                type="file"
                accept="image/*,video/*"
                onChange={onFileChosen}
                disabled={createMedia.isPending}
              />
            </div>

            {createMedia.isPending && (
              <p className={styles.uploaderStatus}>Uploading…</p>
            )}
            {createMedia.error && (
              <p className={styles.uploaderError}>
                Upload failed: {createMedia.error.message}
              </p>
            )}
          </div>
        )}

        <div className={styles.filters}>
          <FilterChip label="All" active={filter === "all"} onClick={() => setFilter("all")} />
          {PURPOSES.map((p) => (
            <FilterChip
              key={p.value}
              label={p.label}
              active={filter === p.value}
              onClick={() => setFilter(p.value)}
            />
          ))}
        </div>

        <div className={styles.filters}>
          {ORIGINS.map((o) => (
            <FilterChip
              key={o.value}
              label={o.label}
              active={origin === o.value}
              onClick={() => setOrigin(o.value)}
            />
          ))}
        </div>

        {mediaQuery.isLoading && <p className={styles.muted}>Loading media…</p>}
        {mediaQuery.error && (
          <p className={styles.error}>Couldn’t load media: {mediaQuery.error.message}</p>
        )}

        {!mediaQuery.isLoading && !mediaQuery.error && media.length === 0 && (
          <p className={styles.empty}>
            No media yet. Upload images (and videos) of your products, logo, or brand
            outings — labeled so the assistant knows how to use them.
          </p>
        )}

        {media.length > 0 && (
          <ul className={styles.grid}>
            {media.map((item) => (
              <MediaCard
                key={item.sk}
                item={item}
                canManage={canManage}
                ws={ws}
                brand={brand}
              />
            ))}
          </ul>
        )}
      </div>
    </AppShell>
  );
}

function FilterChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={`${styles.filterChip} ${active ? styles.filterChipActive : ""}`}
      onClick={onClick}
      aria-pressed={active}
    >
      {label}
    </button>
  );
}

function MediaCard({
  item,
  canManage,
  ws,
  brand,
}: {
  item: MediaItem;
  canManage: boolean;
  ws: string | undefined;
  brand: string | undefined;
}) {
  const id = mediaId(item);
  const updateMedia = useUpdateMedia(ws, brand);
  const deleteMedia = useDeleteMedia(ws, brand);

  const isImage = item.mime.startsWith("image/");
  const isVideo = item.mime.startsWith("video/");

  return (
    <li className={styles.card}>
      <div className={styles.thumb}>
        {item.url && isImage ? (
          <img src={item.url} alt={item.context ?? ""} loading="lazy" />
        ) : item.url && isVideo ? (
          <video src={item.url} controls preload="metadata" />
        ) : (
          <span className={styles.placeholder}>
            <Icon name="image" size={20} />
            {item.url ? item.mime : "Processing…"}
          </span>
        )}
      </div>

      <div className={styles.body}>
        <div className={styles.cardRow}>
          {canManage ? (
            <select
              className={styles.select}
              value={item.purpose}
              onChange={(e) =>
                updateMedia.mutate({ id, purpose: e.target.value as MediaPurpose })
              }
              disabled={updateMedia.isPending}
              aria-label="Purpose"
            >
              {PURPOSES.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
          ) : (
            <span className={styles.purposeTag}>{item.purpose}</span>
          )}

          {canManage && (
            <Btn
              variant="ghost"
              size="sm"
              onClick={() => deleteMedia.mutate({ id })}
              loading={deleteMedia.isPending}
              aria-label="Delete media"
              iconLeft={<Icon name="x" size={13} />}
            >
              Delete
            </Btn>
          )}
        </div>

        {item.context ? (
          <p className={styles.description}>{item.context}</p>
        ) : (
          <p className={styles.descriptionUnset}>No description</p>
        )}

        {canManage ? (
          <label className={styles.approveToggle}>
            <input
              type="checkbox"
              checked={item.approvedForUse}
              onChange={(e) =>
                updateMedia.mutate({ id, approvedForUse: e.target.checked })
              }
              disabled={updateMedia.isPending}
            />
            Approved for use
          </label>
        ) : (
          <span className={styles.approveToggleStatic}>
            <span className={item.approvedForUse ? styles.approved : styles.notApproved}>
              {item.approvedForUse ? "Approved for use" : "Not approved"}
            </span>
          </span>
        )}

        {(updateMedia.error || deleteMedia.error) && (
          <p className={styles.cardError}>
            {updateMedia.error?.message ?? deleteMedia.error?.message}
          </p>
        )}
      </div>
    </li>
  );
}
